import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getStoredColumns, setStoredColumns } from "../../_utils/columnStorage";

/**
 * User-scoped column persistence. Columns stay feature-owned per ADR-F003
 * (this is a sibling to useEntityFilters, not a merge into it) - it just
 * gives every feature the same localStorage-per-user mechanism instead of
 * each reinventing it.
 *
 * Column choices are saved to localStorage keyed by the signed-in user's
 * id, so switching accounts on the same browser never leaks another user's
 * picks.
 *
 * `computeDefaultColumns` only wins while the current user has no stored
 * preference yet - once one exists it's the source of truth, so an async
 * object-customization schema reload can't silently overwrite what a user
 * already picked (the "dead-sync" bug several features' old Redux-mirror
 * useEffect had).
 */
export function useUserColumns({ entityKey, computeDefaultColumns, deps = [] }) {
  const userId = useSelector(
    (state) => state.auth?.user?._id || state.auth?.user?.email || null
  );

  const [customColumns, setCustomColumnsState] = useState(() => {
    const stored = getStoredColumns(entityKey, userId);
    return stored && stored.length > 0 ? stored : computeDefaultColumns();
  });

  const hasStoredRef = useRef(Boolean(getStoredColumns(entityKey, userId)?.length));
  const userIdRef = useRef(userId);

  useEffect(() => {
    if (userIdRef.current !== userId) {
      userIdRef.current = userId;
      const stored = getStoredColumns(entityKey, userId);
      hasStoredRef.current = Boolean(stored?.length);
      setCustomColumnsState(stored && stored.length > 0 ? stored : computeDefaultColumns());
      return;
    }

    if (!hasStoredRef.current) {
      const next = computeDefaultColumns();
      if (Array.isArray(next) && next.length > 0) {
        setCustomColumnsState(next);
      }
    }
    // computeDefaultColumns is intentionally excluded - callers pass a new
    // function identity every render, `deps` is the real change signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, entityKey, ...deps]);

  const setCustomColumns = useCallback(
    (newColumns) => {
      if (!Array.isArray(newColumns)) return;
      hasStoredRef.current = true;
      setCustomColumnsState(newColumns);
      setStoredColumns(entityKey, userId, newColumns);
    },
    [entityKey, userId]
  );

  return [customColumns, setCustomColumns];
}
